import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Modal } from "react-bootstrap"
import { SearchDetail } from "components/utils/search"
import { APIResponse } from "redux/apis/types"
import {
  changeSymbol,
  changeSubSymbol,
  changeRelativeChartFlag,
} from "redux/ducks/Chart"
import { PLOTTYPE } from "../logic/chartConfig"
import { clone, set, get, isEmpty, map, merge, includes, filter } from "lodash"
import * as Formatter from "utils/Formatter"
import { FOREIGN_STOCK, SECURITY_TYPE } from "config/const"
import ClassNames from "classnames"
import APIs from "redux/apis"
import { BaseSelect } from "components/atoms/input/select"
import { getCountryIcon } from "utils/util"
import { PUT_OR_CALL } from "config/const"
import { useEffectFirstSkip } from "components/actions/customHook/useEffectFirstSkip"

interface props {
  canvasId: string
}

const RelativeSymbolSelector = React.memo((props: props) => {
  const { canvasId } = props
  const [modalVisible, setModalVisible] = useState(false)
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const plotType = useSelector(plotTypeSelector)
  const PandFFlag = useMemo(() => {
    return plotType.id === PLOTTYPE[3].id
  }, [plotType])

  return (
    <div className="btn-multi-control">
      <label
        className={ClassNames("btn btn-outline shaped", {
          disabled: PandFFlag,
        })}
      >
        <input
          type="checkbox"
          className="input-multi-control"
          onChange={() => {
            setModalVisible(!modalVisible)
          }}
          checked={modalVisible}
        />
        <span className="ripple">比較チャート</span>
      </label>
      <RelativeSymbolSelectorModal
        canvasId={canvasId}
        isVisible={modalVisible}
        setVisible={setModalVisible}
      />
    </div>
  )
})

interface modalProps {
  canvasId: string
  isVisible: boolean
  setVisible: Function
}
const RelativeSymbolSelectorModal = React.memo((props: modalProps) => {
  const { canvasId, isVisible, setVisible } = props
  const dispatch = useDispatch()
  const symbolSelector = (state: any) => state.chart[canvasId].symbol
  const subSymbolSelector = (state: any) => state.chart[canvasId].subSymbol
  const symbol = useSelector(symbolSelector)
  const subSymbol = useSelector(subSymbolSelector)
  const [subSymbols, setSubSymbols] = useState(subSymbol)
  const [subSymbolVisible, setSubChartVisible] = useState([false, false])
  const mainLabelVisible = useMemo(() => {
    const targetSecurityType = get(symbol, "SecurityType")
    return (
      includes(FOREIGN_STOCK, targetSecurityType) ||
      SECURITY_TYPE.Equity.id === targetSecurityType
    )
  }, [symbol])
  const subLabelVisible = useCallback(
    (id: number) => {
      const targetSecurityType = get(subSymbols[id], "SecurityType")
      return (
        includes(FOREIGN_STOCK, targetSecurityType) ||
        SECURITY_TYPE.Equity.id === targetSecurityType
      )
    },
    [subSymbols]
  )

  const onHide = () => {
    setVisible(false)
  }

  const _setSubSymbol = (id: number, symbolData: object) => {
    const _subSymbols = clone(subSymbols)
    set(_subSymbols, id, symbolData)
    setSubSymbols(_subSymbols)
    !isEmpty(symbol) && _setSubChartVisible(id, true)
  }

  const _setSubChartVisible = (id: number, visible = !subSymbolVisible[id]) => {
    const _subSymbolVisible = clone(subSymbolVisible)
    setSubChartVisible(set(_subSymbolVisible, id, visible))
  }

  useEffect(() => {
    const _subSymbols = map(subSymbols, (items, index: number) => {
      return merge(items, { visible: subSymbolVisible[index] })
    })
    dispatch(changeSubSymbol(canvasId, _subSymbols))
  }, [subSymbols, subSymbolVisible])

  useEffectFirstSkip(() => {
    if (includes(subSymbolVisible, true))
      dispatch(changeRelativeChartFlag(canvasId, true))
    if (!includes(subSymbolVisible, true))
      dispatch(changeRelativeChartFlag(canvasId, false))
  }, [subSymbolVisible])

  return (
    <Modal
      show={isVisible}
      dialogClassName="chart-setting-modal relative-symbol-modal"
    >
      <Modal.Header>
        <div className="header-title">比較チャート設定</div>
        <div className="header-nav-icon-col">
          <div
            className="header-icon ripple"
            data-izimodal-close
            onClick={() => onHide()}
          >
            <i className="material-icons">close</i>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-content relative-symbol-selector">
          <div className="relative-symbol-selector-row">
            <div className="relative-symbol-selector-title">
              <label className="checkbox-control">
                <input type="checkbox" checked={!isEmpty(symbol)} />
                <span>銘柄１(主要銘柄)</span>
              </label>
              <SearchDetail
                className="search-bar active"
                placeholder="銘柄コード/銘柄名"
                initialState={{ searchValue: get(symbol, "Symbol", "") }}
                executeFunction={(data: APIResponse.Res) =>
                  dispatch(changeSymbol(canvasId, data))
                }
                hasCountryBtn={true}
                isDirect={true}
                chartType={1}
                inputId="search-relative-1"
              />
            </div>
            <div className="relative-symbol-selector-contents">
              <div className="relative-symbol-selector-symbol-group">
                {Formatter.symbol(symbol) !== "-" && mainLabelVisible && (
                  <div className="relative-symbol-selector-symbol-items symbol-code-label">
                    {Formatter.symbol(symbol)}
                  </div>
                )}
                <div className="relative-symbol-selector-symbol-items">
                  {get(symbol, "SymbolNameS") || get(symbol, "SymbolName")}
                </div>
              </div>
            </div>
          </div>
          <div className="relative-symbol-selector-row">
            <div className="relative-symbol-selector-title">
              <label className="checkbox-control">
                <input
                  type="checkbox"
                  disabled={isEmpty(symbol) || !get(subSymbols, [0, "Symbol"])}
                  checked={subSymbolVisible[0]}
                  onChange={() => _setSubChartVisible(0)}
                />
                <span>銘柄２(比較銘柄)</span>
              </label>
              {/* <SearchDetail
                className="search-bar active"
                placeholder="銘柄コード/銘柄名"
                initialState={{
                  searchValue: get(subSymbols, [0, "Symbol"], ""),
                }}
                executeFunction={(data: APIResponse.Res) => {
                  _setSubSymbol(0, data)
                  !isEmpty(symbol) && _setSubChartVisible(0, true)
                }}
                hasCountryBtn={true}
                isDirect={true}
                chartType={true}
              /> */}
            </div>
            <div>
              <SymbolSelector
                setSubSymbol={(data: object) => _setSubSymbol(0, data)}
                relativeNum={2}
              />
            </div>
            <div className="relative-symbol-selector-contents">
              <div className="relative-symbol-selector-symbol-group">
                {subLabelVisible(0) &&
                  Formatter.symbol(subSymbols[0]) !== "-" && (
                    <div className="relative-symbol-selector-symbol-items symbol-code-label">
                      {!isEmpty(subSymbols[0]) &&
                        Formatter.symbol(subSymbols[0])}
                    </div>
                  )}
                <div className="relative-symbol-selector-symbol-items">
                  {get(subSymbols, [0, "SymbolNameS"]) ||
                    get(subSymbols, [0, "SymbolName"])}
                </div>
              </div>
            </div>
          </div>
          <div className="relative-symbol-selector-row">
            <div className="relative-symbol-selector-title">
              <label className="checkbox-control">
                <input
                  type="checkbox"
                  disabled={isEmpty(symbol) || !get(subSymbols, [1, "Symbol"])}
                  checked={subSymbolVisible[1]}
                  onChange={() => _setSubChartVisible(1)}
                />
                <span>銘柄３(比較銘柄)</span>
              </label>
              {/* <SearchDetail
                className="search-bar active"
                placeholder="銘柄コード/銘柄名"
                initialState={{
                  searchValue: get(subSymbols, [1, "Symbol"], ""),
                }}
                executeFunction={(data: APIResponse.Res) => {
                  _setSubSymbol(1, data)
                  !isEmpty(symbol) && _setSubChartVisible(1, true)
                }}
                hasCountryBtn={true}
                isDirect={true}
                chartType={1}
              /> */}
            </div>
            <div>
              <SymbolSelector
                setSubSymbol={(data: object) => _setSubSymbol(1, data)}
                relativeNum={3}
              />
            </div>
            <div className="relative-symbol-selector-contents">
              <div className="relative-symbol-selector-symbol-group">
                {subLabelVisible(1) &&
                  Formatter.symbol(subSymbols[1]) !== "-" && (
                    <div className="relative-symbol-selector-symbol-items symbol-code-label">
                      {Formatter.symbol(subSymbols[1])}
                    </div>
                  )}
                <div className="relative-symbol-selector-symbol-items">
                  {get(subSymbols, [1, "SymbolNameS"]) ||
                    get(subSymbols, [1, "SymbolName"])}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="modal-bottom-btn-row">
          <div
            className="btn btn-outline ripple"
            data-izimodal-close
            onClick={() => onHide()}
          >
            閉じる
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
})

const HEADER_LABEL = [
  { id: 0, name: "国内株式" },
  { id: 1, name: "外国株式" },
  { id: 2, name: "主要指数" },
  { id: 3, name: "国内指数" },
  { id: 4, name: "外国指数" },
  { id: 5, name: "先物" },
  { id: 6, name: "オプション" },
  { id: 7, name: "為替" },
  { id: 8, name: "商品" },
  { id: 9, name: "国債" },
]

const FOREIN_INDEX_MENU = [
  { id: 0, name: "米国", icon: "icon-us" },
  { id: 1, name: "香港", icon: "icon-hong-kong" },
  { id: 2, name: "中国", icon: "icon-china" },
  { id: 3, name: "ベトナム", icon: "icon-vietnam" },
  { id: 4, name: "韓国", icon: "icon-korea" },
  { id: 5, name: "台湾", icon: "icon-taiwan" },
  { id: 6, name: "シンガポール", icon: "icon-singapore" },
  { id: 7, name: "タイ", icon: "icon-thailand" },
  { id: 8, name: "マレーシア", icon: "icon-malaysia" },
  { id: 9, name: "インドネシア", icon: "icon-indonesia" },
  { id: 10, name: "フィリピン", icon: "icon-philippines" },
  { id: 11, name: "イスラエル", icon: "icon-israel" },
]

const BOND_MENU = [
  { id: 0, name: "タイ", icon: "icon-thailand" },
  { id: 1, name: "中国", icon: "icon-china" },
  { id: 2, name: "日本", icon: "icon-jp" },
  { id: 3, name: "香港", icon: "icon-hong-kong" },
  { id: 4, name: "米国", icon: "icon-us" },
  { id: 5, name: "南アフリカ", icon: "icon-south-africa" },
  { id: 6, name: "韓国", icon: "icon-korea" },
  { id: 7, name: "フィリピン", icon: "icon-philippines" },
  { id: 8, name: "インドネシア", icon: "icon-indonesia" },
  { id: 9, name: "台湾", icon: "icon-taiwan" },
  { id: 10, name: "英国", icon: "icon-uk" },
  { id: 11, name: "マレーシア", icon: "icon-malaysia" },
  { id: 12, name: "ドイツ", icon: "icon-germany" },
  { id: 13, name: "フランス", icon: "icon-france" },
  { id: 14, name: "シンガポール", icon: "icon-singapore" },
  { id: 15, name: "ニュージーランド", icon: "icon-new-zealand" },
  { id: 16, name: "オーストラリア", icon: "icon-au" },
  { id: 17, name: "ブラジル", icon: "icon-brazil" },
]

const FUTURE_MENU = [
  { id: 0, name: "日経225先物" },
  { id: 1, name: "日経225ミニ先物" },
  { id: 2, name: "JPX日経400先物" },
  { id: 3, name: "TOPIX先物" },
  { id: 4, name: "TOPIXミニ先物" },
  { id: 5, name: "CORE30先物" },
  { id: 6, name: "REIT先物" },
]

const FOREIN_INDEX_PARAM = [
  "FC.US.Index",
  "FC.HK.Index",
  "FC.CN.Index",
  "FC.VN.Index",
  "FC.KR.Index",
  "FC.TW.Index",
  "FC.SG.Index",
  "FC.TH.Index",
  "FC.MY.Index",
  "FC.ID.Index",
  "FC.PH.Index",
  "FC.IL.Index",
]

const BOND_PARAM = [
  "Top.GB.TH",
  "Top.GB.CN",
  "Top.GB.JP",
  "Top.GB.HK",
  "Top.GB.US",
  "Top.GB.ZA",
  "Top.GB.KR",
  "Top.GB.PH",
  "Top.GB.ID",
  "Top.GB.TW",
  "Top.GB.GB",
  "Top.GB.MY",
  "Top.GB.DE",
  "Top.GB.FR",
  "Top.GB.SG",
  "Top.GB.NZ",
  "Top.GB.AU",
  "Top.GB.BR",
]

const FUTURE_PARAM = [101, 901, 121, 107, 907, 171, 155]

const MAIN_INDEX_PARAM = "Top.Index.Main"
const JPN_INDEX_PARAM = "Jpn.Index.All"
const FX_PARAM = "Top.FX.JPY"
const COMMODITY_PARAM = "Top.Commodity"

export const SearchCountryTypeContext = React.createContext({ type: 0 })

const SymbolSelector = (props: {
  setSubSymbol: Function
  relativeNum: number
}) => {
  const [selectedCategory, setSelectedCategory] = useState(0)
  const [selectedSubCategory, setSelectedSubCategory] = useState(0)
  const [selectedOption, setSelectedOption] = useState(0)
  const [optionList, setOptionList] = useState([])
  const [optionData, setOptionData] = useState([])
  const [countryType, setCountryType] = useState(0)

  const _setOptionData = (res: object) => {
    const resultList = get(res, "ResultList", [])
    setOptionData(resultList)
    setOptionList(
      map(resultList, (items, id) => {
        const icon = getCountryIcon(items)
        const data = { id, name: get(items, "SymbolName") }
        return icon ? merge(data, { icon: icon }) : data
      })
    )
  }

  const createData = () => {
    if (includes([2, 3, 4, 7, 8, 9], selectedCategory)) {
      const param =
        selectedCategory === 2
          ? MAIN_INDEX_PARAM
          : selectedCategory === 3
          ? JPN_INDEX_PARAM
          : selectedCategory === 4
          ? FOREIN_INDEX_PARAM[selectedSubCategory]
          : selectedCategory === 7
          ? FX_PARAM
          : selectedCategory === 8
          ? COMMODITY_PARAM
          : BOND_PARAM[selectedSubCategory]
      if (!param) return
      ;(async () => {
        const res = await APIs.SymbolDetail.PriceList({
          Name: param,
        })
        _setOptionData(res)
      })()
    }
    if (selectedCategory === 5) {
      if (!FUTURE_PARAM[selectedSubCategory]) return
      ;(async () => {
        const res = await APIs.SymbolDetail.SymbolList({
          Exchange: 2,
          TargetSecurityTypes: FUTURE_PARAM[selectedSubCategory],
          MajorMonthOnly: true,
        })
        _setOptionData(res)
      })()
    }
  }

  useEffect(() => {
    setSelectedSubCategory(0)
    setSelectedOption(0)
    if (includes([0, 1], selectedCategory))
      return setCountryType(selectedCategory)
    createData()
  }, [selectedCategory])

  useEffect(() => {
    setSelectedOption(0)
    createData()
  }, [selectedSubCategory])

  useEffect(() => {
    if (isEmpty(optionData)) return
    props.setSubSymbol(optionData[selectedOption])
  }, [selectedOption])

  return (
    <SearchCountryTypeContext.Provider value={{ type: countryType }}>
      <div className="symbol-selecter-wrapper relative-symbol-selector-title">
        <div className="relative-symbol-selector category-selector">
          <BaseSelect
            option={HEADER_LABEL}
            onChange={(id: number) => setSelectedCategory(id)}
            value={selectedCategory}
          />
        </div>
        {includes([0, 1], selectedCategory) && (
          <SearchDetail
            className="search-bar active"
            placeholder="銘柄コード/銘柄名"
            initialState={{}}
            executeFunction={(data: APIResponse.Res) => {
              props.setSubSymbol(data)
              // !isEmpty(symbol) && _setSubChartVisible(1, true)
            }}
            hasCountryBtn={true}
            isDirect={true}
            noMemory={true}
            chartType={2}
            inputId={`search-relative-${props.relativeNum}`}
          />
        )}
        {includes([4, 5, 9], selectedCategory) && (
          <div
            className={ClassNames(
              "relative-symbol-selector sub-category-selector",
              {
                "foreign-index-sub-selector": selectedCategory === 4,
                "futur-sub-selector": selectedCategory === 5,
                "bond-sub-selector": selectedCategory === 9,
              }
            )}
          >
            <BaseSelect
              option={
                selectedCategory === 4
                  ? FOREIN_INDEX_MENU
                  : selectedCategory === 5
                  ? FUTURE_MENU
                  : BOND_MENU
              }
              value={selectedSubCategory}
              onChange={(id: number) => {
                setSelectedSubCategory(id)
              }}
            />
          </div>
        )}
        {!isEmpty(optionList) && !includes([0, 1, 6], selectedCategory) && (
          <div
            className={ClassNames(
              "relative-symbol-selector symbol-name-selector",
              {
                "main-index-selector": selectedCategory === 2,
                "domestic-index-selector": selectedCategory === 3,
                "foreign-index-selector": selectedCategory === 4,
                "futur-selector": selectedCategory === 5,
                "fx-selector": selectedCategory === 7,
                "commodity-selector": selectedCategory === 8,
                "bond-selector": selectedCategory === 9,
              }
            )}
          >
            <BaseSelect
              option={optionList}
              value={selectedOption}
              onChange={(id: number) => {
                if (id === selectedOption)
                  props.setSubSymbol(optionData[selectedOption])
                setSelectedOption(id)
              }}
            />
          </div>
        )}
        {selectedCategory === 6 && (
          <OptionSelecter setSubSymbol={props.setSubSymbol} />
        )}
      </div>
    </SearchCountryTypeContext.Provider>
  )
}

const OptionSelecter = (props: { setSubSymbol: Function }) => {
  const [selectedYearMonth, setSelectedYearMonth] = useState<number>(null)
  const [yearMonthList, setYearMonthList] = useState([])
  const [putData, setPutData] = useState([])
  const [callData, setCallData] = useState([])
  const [selectedPutOrCall, setSelectedPutOrCall] = useState(0)
  const [filterList, setFilterList] = useState([])
  const [stkList, setStkList] = useState([])
  const [selectedSymbol, setSelectedSymbol] = useState(0)

  useEffect(() => {
    ;(async () => {
      const data = await APIs.SymbolDetail.OptionSymbolList({
        TargetSecurityTypes: "103",
        Exchange: "2",
      })
      const yearMonth = get(data, "ResultList", [])
      setFilterList(yearMonth)
      setYearMonthList(
        map(yearMonth, (str: string, i: number) => {
          return { id: i, name: `${Formatter.date(str, "YYYY/MM")}限月` }
        })
      )
      setSelectedYearMonth(0)
    })()
  }, [])

  useEffect(() => {
    if (!selectedYearMonth && selectedYearMonth !== 0) return
    ;(async () => {
      const data = await APIs.SymbolDetail.SymbolList({
        TargetSecurityTypes: "103",
        Exchange: "2",
        YearMonth: filterList[selectedYearMonth],
      })
      const resultList = get(data, "ResultList", [])
      const putResultList = filter(resultList, {
        PutOrCall: PUT_OR_CALL.PUT.id,
      })
      const callResulList = filter(resultList, {
        PutOrCall: PUT_OR_CALL.CALL.id,
      })
      setStkList(
        map(putResultList, (data: APIResponse.Res, id: number) => {
          return {
            id: id,
            name: get(data, "StrikePrice", null),
          }
        })
      )
      setPutData(putResultList)
      setCallData(callResulList)
    })()
  }, [selectedYearMonth])

  useEffectFirstSkip(() => {
    if (selectedPutOrCall === 0) props.setSubSymbol(callData[selectedSymbol])
    if (selectedPutOrCall === 1) props.setSubSymbol(putData[selectedSymbol])
  }, [selectedSymbol])

  return (
    <>
      <div className="relative-symbol-selector year-month-selector">
        <BaseSelect
          option={yearMonthList}
          onChange={setSelectedYearMonth}
          value={selectedYearMonth}
        />
      </div>
      <div className="relative-symbol-selector call-put-selector">
        <BaseSelect
          option={[{ id: 0, name: "コール" }, { id: 1, name: "プット" }]}
          value={selectedPutOrCall}
          onChange={setSelectedPutOrCall}
        />
      </div>
      <div className="relative-symbol-selector option-price-selector">
        <BaseSelect
          option={stkList}
          onChange={(id: number) => {
            if (id !== selectedSymbol) return setSelectedSymbol(id)
            if (selectedPutOrCall === 0)
              props.setSubSymbol(callData[selectedSymbol])
            if (selectedPutOrCall === 1)
              props.setSubSymbol(putData[selectedSymbol])
          }}
          value={selectedSymbol}
        />
      </div>
    </>
  )
}

export default RelativeSymbolSelector
